import type { ReactNode } from 'react';
import { ApiError } from '@lombok/api-client';
import { Button } from '@lombok/ui';
import { TableSkeleton } from './Skeleton';

/**
 * Standard Spanish loading / error / empty states. Every list and detail view
 * should route its query through this so the three states are handled
 * consistently (a docs/11 UX requirement) instead of ad-hoc per screen.
 */

export function LoadingState({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-text-muted">
      <span
        className="h-7 w-7 animate-spin rounded-full border-2 border-border-strong border-t-brand-accent"
        aria-hidden="true"
      />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function EmptyState({
  title = 'Sin resultados',
  description,
  action,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border-strong bg-surface py-16 text-center">
      <p className="text-base font-medium text-text-primary">{title}</p>
      {description ? <p className="max-w-sm text-sm text-text-muted">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message =
    error instanceof ApiError
      ? error.message
      : 'No se pudieron cargar los datos. Intenta de nuevo.';
  const requestId = error instanceof ApiError ? error.requestId : undefined;

  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-danger-soft bg-danger-soft/60 py-12 text-center">
      <p className="text-base font-semibold text-danger">Ocurrió un problema</p>
      <p className="max-w-md text-sm text-text-primary">{message}</p>
      {requestId ? (
        <p className="text-xs text-text-muted">Código de referencia: {requestId}</p>
      ) : null}
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Reintentar
        </Button>
      ) : null}
    </div>
  );
}

interface QueryStateProps {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  isEmpty?: boolean;
  onRetry?: () => void;
  loadingLabel?: string;
  /**
   * Loading appearance. `'table'` shows a layout-shaped shimmer skeleton (lists);
   * `'spinner'` (default) shows the centered spinner (detail/standalone views).
   */
  loadingVariant?: 'spinner' | 'table';
  /** Column count for the table skeleton (matches the real table's columns). */
  skeletonCols?: number;
  /** Row count for the table skeleton. */
  skeletonRows?: number;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  children: ReactNode;
}

/** Renders the right state for a query, or `children` when data is ready. */
export function QueryState({
  isLoading,
  isError,
  error,
  isEmpty = false,
  onRetry,
  loadingLabel,
  loadingVariant = 'spinner',
  skeletonCols = 4,
  skeletonRows = 6,
  emptyTitle,
  emptyDescription,
  emptyAction,
  children,
}: QueryStateProps) {
  if (isLoading)
    return loadingVariant === 'table' ? (
      <TableSkeleton rows={skeletonRows} cols={skeletonCols} />
    ) : (
      <LoadingState label={loadingLabel} />
    );
  if (isError) return <ErrorState error={error} onRetry={onRetry} />;
  if (isEmpty)
    return (
      <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
    );
  return <>{children}</>;
}
